/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  Image,
  Box,
  useToast,
  VStack,
  Select
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { log } from '../../utils/logger'
import { createDashboardRolesApi } from './../../utils/api-interface'
import {
  CreateDashboardProfileRequest,
  DashboardProfilesList,
  DashboardRolesList,
  DashboardProfile,
  CreateDashboardProfileResponse,
  DashboardRole
} from '../../services/network'
import axios from 'axios'
import { getErrorMessage, isValidEmail } from '../../utils/utils'

interface ICreateDashboardProfileRoleModalProps {
  isOpen: boolean
  onClose: () => void
  profiles: DashboardProfilesList | undefined
  setProfiles: React.Dispatch<React.SetStateAction<DashboardProfilesList | undefined>>
}

function CreateDashboardProfileRoleModal({ isOpen, onClose, profiles, setProfiles }: ICreateDashboardProfileRoleModalProps): JSX.Element {
  const { t } = useTranslation()
  const toast = useToast()
  const [firstName, setFirstName] = useState('')
  const handleFirstName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFirstName(e.target.value)
  }
  const [lastName, setLastName] = useState('')
  const handleLastName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setLastName(e.target.value)
  }
  const [email, setEmail] = useState('')
  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value)
  }
  const [roleSelected, setRoleSelected] = useState('')

  const [createDashboardProfileRequest, setCreateDashboardProfileRequest] = useState<CreateDashboardProfileRequest>()
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(true)
  const [loading, isLoading] = useState(false)

  const [roles, setRoles] = useState<DashboardRolesList>()
  const handleRoleChange = (e: { target: { value: React.SetStateAction<string> } }): void => {
    setRoleSelected(e.target.value)
  }

  const fetchDashboardRoles = async (): Promise<void> => {
    try {
      const dashboardRolesApi = createDashboardRolesApi()
      const response = await dashboardRolesApi.getDashboardRoles()
      setRoles(response.data)
    } catch (error) {
      log.error('Error fetching roles:', error)
    }
  }

  const handleClose = useCallback((): void => {
    if (loading) {
      return
    }
    onClose()
    setFirstName('')
    setLastName('')
    setEmail('')
    setRoleSelected('')
    setCreateDashboardProfileRequest(undefined)
  }, [loading, onClose])

  const getRoleTitleById = useCallback(
    (roleId: string): string => {
      const role = roles?.dashboardRoles.find((role) => role.id === roleId)
      if (!role) {
        throw new Error(`Role with id ${roleId} not found`)
      }
      return role.title
    },
    [roles?.dashboardRoles]
  )

  const mapRoleIdToRole = useCallback(
    (roleId: string): DashboardRole => {
      const roleTitle = getRoleTitleById(roleId)
      return {
        id: roleId,
        title: roleTitle
      }
    },
    [getRoleTitleById]
  )

  const transformToDashboardProfile = useCallback(
    (response: CreateDashboardProfileResponse): DashboardProfile => {
      return {
        id: response.id,
        firstName: response.firstName,
        lastName: response.lastName,
        email: response.email,
        role: mapRoleIdToRole(response.roleId)
      }
    },
    [mapRoleIdToRole]
  )

  const postNewDashboardProfile = useCallback(async (): Promise<void> => {
    if (loading) {
      return
    }
    if (createDashboardProfileRequest) {
      try {
        isLoading(true)
        const dashboardRolesApi = createDashboardRolesApi()
        const response = await dashboardRolesApi.createDashboardProfile(createDashboardProfileRequest)
        log.debug(response.data)
        toast({
          title: t('dashboard_profile_created_toast'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setProfiles((prevProfiles) => {
          const updatedProfiles = prevProfiles ? [...prevProfiles.dashboardProfiles] : []
          const newProfile = transformToDashboardProfile(response.data as CreateDashboardProfileResponse)
          updatedProfiles.push(newProfile)
          return { dashboardProfiles: updatedProfiles }
        })

        handleClose()
      } catch (error) {
        toast({
          title: t('dashboard_profile_not_created_toast'),
          description: `${getErrorMessage(error)}`,
          status: 'error',
          duration: 2000,
          isClosable: true
        })
      }
      isLoading(false)
    }
  }, [createDashboardProfileRequest, handleClose, loading, setProfiles, t, toast, transformToDashboardProfile])

  useEffect(() => {
    if (firstName && lastName && isValidEmail(email) && roleSelected) {
      setConfirmButtonDisabled(false)

      setCreateDashboardProfileRequest({
        firstName: firstName,
        lastName: lastName,
        email: email,
        roleId: roleSelected
      })
    } else {
      setConfirmButtonDisabled(true)
    }
  }, [firstName, lastName, email, roleSelected])

  useEffect(() => {
    eva.replace()
    fetchDashboardRoles()
  }, [])

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered={true}>
        <ModalOverlay />
        <ModalContent marginBottom={'0px'} borderRadius={'26px 26px 0px 0px'}>
          <ModalHeader fontSize='24px' fontWeight='700'>
            {t('create_dashboard_profile')}
          </ModalHeader>

          <ModalBody>
            <FormControl id='firstName'>
              <FormLabel variant={'primary'}>{t('first_name')}</FormLabel>
              <Input bg='white' value={firstName} onChange={handleFirstName} />
            </FormControl>

            <FormControl id='lastName' mt={'10px'}>
              <FormLabel variant={'primary'}>{t('last_name')}</FormLabel>
              <Input bg='white' value={lastName} onChange={handleLastName} />
            </FormControl>

            <FormControl id='email' mt={'10px'}>
              <FormLabel variant={'primary'}>{t('email')}</FormLabel>
              <Input bg='white' value={email} onChange={handleEmail} />
            </FormControl>

            <FormControl id='role'>
              <FormLabel variant={'primary'} marginTop={'10px'}>
                {t('role')}
              </FormLabel>
              <Select variant={'primaryBlack'} placeholder={t('placeholder_role_select')} onChange={handleRoleChange} value={roleSelected}>
                {roles &&
                  roles.dashboardRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {t(`sidebar_roles.${role.title}`)}
                    </option>
                  ))}
              </Select>
            </FormControl>
          </ModalBody>

          <ModalFooter justifyContent='center'>
            <Button
              variant='outline'
              width={'50%'}
              height='50px'
              color={'primary.400'}
              borderColor={'primary.400'}
              onClick={handleClose}
              margin={'5px'}
              isDisabled={loading}>
              {t('cancel')}
            </Button>
            <Button
              variant={'primary'}
              width={'50%'}
              height='50px'
              isDisabled={confirmButtonDisabled}
              onClick={postNewDashboardProfile}
              margin={'5px'}
              isLoading={loading}>
              {t('create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export { CreateDashboardProfileRoleModal }
